
import React from 'react';
import CourseTemplate from "../../components/CourseTemplate";
import DentalSlideshowBackground from '../../components/DentalSlideshowBackground';

const BDSCoursePage: React.FC = () => {
  const courseData = {
    title: "Bachelor of Dental Surgery",
    shortName: "BDS",
    duration: "5 years",
    description: "A comprehensive undergraduate program in dental sciences focusing on oral health, dental procedures, and patient care.",
    overview: "Bachelor of Dental Surgery (BDS) is a 5-year undergraduate professional degree program that focuses on the study of dentistry, oral health, and dental procedures. The program combines theoretical knowledge with extensive practical training to prepare students for careers in dental healthcare. Students learn about oral anatomy, dental diseases, treatment procedures, and develop practical skills in dental care and surgery.",
    
    eligibility: [
      "12th grade with Physics, Chemistry, Biology (PCB)",
      "Minimum 50% aggregate marks (40% for SC/ST/OBC)",
      "NEET UG qualification mandatory",
      "Age: 17-25 years (relaxation for reserved categories)"
    ],

    careerProspects: {
      roles: [
        "General Dentist",
        "Oral Surgeon", 
        "Orthodontist",
        "Periodontist",
        "Pediatric Dentist",
        "Endodontist",
        "Prosthodontist",
        "Dental Consultant"
      ],
      industries: [
        "Private Dental Clinics",
        "Government Hospitals",
        "Dental Colleges & Universities", 
        "Corporate Healthcare",
        "Medical Tourism",
        "Pharmaceutical Companies",
        "Dental Equipment Manufacturing",
        "Insurance Companies"
      ],
      averageSalary: "₹3,00,000 - ₹12,00,000 per year",
      topRecruiters: [
        "Apollo Hospitals",
        "Fortis Healthcare",
        "Max Healthcare",
        "Government Health Departments",
        "Private Dental Chains",
        "International Dental Clinics"
      ]
    },

    curriculum: [
      {
        year: 1,
        subjects: [
          "Human Anatomy",
          "Human Physiology", 
          "Dental Anatomy & Embryology",
          "General Pathology",
          "Biochemistry",
          "Microbiology"
        ]
      },
      {
        year: 2, 
        subjects: [
          "General Pathology & Microbiology",
          "General Medicine",
          "General Surgery",
          "Dental Materials",
          "Pre-clinical Prosthodontics",
          "Pre-clinical Conservative Dentistry"
        ]
      },
      {
        year: 3,
        subjects: [
          "Oral Pathology",
          "Pharmacology & Therapeutics",
          "Oral Medicine & Radiology",
          "Prosthodontics",
          "Conservative Dentistry & Endodontics",
          "Periodontology"
        ]
      },
      {
        year: 4,
        subjects: [
          "Oral & Maxillofacial Surgery",
          "Pediatric Dentistry",
          "Orthodontics", 
          "Community Dentistry & Preventive Medicine",
          "Clinical Prosthodontics",
          "Clinical Conservative Dentistry"
        ]
      },
      {
        year: 5,
        subjects: [
          "Internship/Rotatory Internship",
          "Clinical Postings in all departments",
          "Community Health Programs",
          "Research Project",
          "Hospital-based training",
          "Rural posting experience"
        ]
      }
    ],

    admissionProcess: [
      "Qualify NEET UG examination",
      "Register for state/national counseling",
      "Choice filling and locking",
      "Document verification",
      "Seat allocation based on rank and preference",
      "Fee payment and admission confirmation"
    ],

    topColleges: [
      { 
        name: "Maulana Azad Institute of Dental Sciences", 
        location: "Delhi", 
        fees: "₹1,00,000/year",
        ranking: "1"
      },
      { 
        name: "King George's Medical University", 
        location: "Lucknow", 
        fees: "₹1,50,000/year",
        ranking: "2"
      },
      { 
        name: "Government Dental College", 
        location: "Mumbai", 
        fees: "₹2,00,000/year",
        ranking: "3"
      },
      { 
        name: "Tamil Nadu Government Dental College", 
        location: "Chennai", 
        fees: "₹1,80,000/year",
        ranking: "4"
      },
      { 
        name: "Dr. R Ahmed Dental College", 
        location: "Kolkata", 
        fees: "₹3,00,000/year",
        ranking: "5"
      },
      { 
        name: "Manipal College of Dental Sciences", 
        location: "Karnataka", 
        fees: "₹8,00,000/year",
        ranking: "6"
      }
    ],

    skills: [
      "Manual dexterity and precision",
      "Patient communication skills", 
      "Problem-solving abilities",
      "Attention to detail",
      "Clinical decision making",
      "Empathy and patience",
      "Technical proficiency with dental equipment",
      "Time management and organization"
    ],

    futureScope: "BDS graduates have excellent career prospects with opportunities for specialization through MDS programs, private practice establishment, hospital appointments, academic careers, research opportunities, and international practice. The dental healthcare sector is experiencing rapid growth with increasing awareness about oral health and cosmetic dentistry.",

    faqs: [
      {
        question: "What is the admission process for BDS?",
        answer: "Admission to BDS is primarily through NEET UG examination. Candidates must qualify NEET and participate in state or national counseling processes based on their ranks and category."
      },
      {
        question: "Is BDS a good career option?",
        answer: "Yes, BDS offers excellent career prospects with opportunities for private practice, specialization, and good earning potential. The dental healthcare sector is growing rapidly in India."
      },
      {
        question: "What are the specialization options after BDS?",
        answer: "After BDS, you can pursue MDS in specializations like Orthodontics, Oral Surgery, Periodontology, Prosthodontics, Pediatric Dentistry, Oral Pathology, and Conservative Dentistry."
      },
      {
        question: "Can I start my own dental clinic after BDS?",
        answer: "Yes, after completing BDS and the mandatory internship, you can register with the dental council and start your own practice. Many graduates successfully run private dental clinics."
      },
      {
        question: "What is the scope of BDS abroad?",
        answer: "BDS graduates can pursue practice abroad, but most countries require additional licensing exams. Countries like UAE, Saudi Arabia, and other Gulf nations have good opportunities for Indian dental graduates."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {courseData.title}
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={courseData} />
      </div>
    </div>
  );
};

export default BDSCoursePage;
